import React, {useState, useEffect} from 'react';
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { isRegister, userToken, logout } from "../../features/user";
import axios from "axios";
import { API_OK, API_URL, DEFAULT_ERROR_TEXT, API_EXCEPTION } from '../../Constants';
import {Trans,  useTranslation } from 'react-i18next';
import {checkApiSessionErrorMessage, getLang} from "../../Helper"
import { useRetryCountdown } from "../../hooks/useRetryCountdown";
import RetryNotice from "../widgets/RetryNotice";

import Frame from '../widgets/Frame';
import { useSubmitGuard } from "../../hooks/useSubmitGuard";
import { useAutoFocus } from "../../hooks/useAutoFocus";
import { useCaptcha } from "../../captcha";
import { useEnterSubmit } from '../../hooks/useEnterSubmit';

export function VerifyEmail() {
    const [state, setState] = useState({ code: "", errors: {}, message: null, email: "" });


    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { t } = useTranslation();
    // One check per click: each submission spends a verification attempt.
    const submit = useSubmitGuard();
    /**
     * הקאפצ'ה. פעילה רק כשההגדרה הגיעה מהשרת, ומה שנשלח נבדק שם בלי קשר למסך.
     *
     * טוקן אחד למסך: השליחה מחדש והבדיקה חולקות פעולה, כי הן חולקות טופס.
     */
    const captcha = useCaptcha('verifyCode');
    const isUserRegister = useSelector(isRegister)
    const retry = useRetryCountdown();
    /* הסמן בשדה מהטעינה - הקוד יצא לפני שהמסך נטען - ו-`focusCode` להחזרתו אחרי
       שליחה חוזרת, שנשארת באותו מסך ומשאירה את הסמן על הקישור שנלחץ. */
    const [codeRef, focusCode] = useAutoFocus();


    useEffect(()=>{
        if(!isUserRegister){
           navigate("/register");
        } else {
            getRegistrationData();
        }
        // eslint-disable-next-line
    },[]);


    const getRegistrationData = async (e) => {
        let errorMessage = DEFAULT_ERROR_TEXT;
        
        let config = isUserRegister?{'headers': {"token": userToken(), "lang": getLang()}}:{'headers': {"lang": getLang()}};
        try {
            const reply = await axios.get(API_URL + 'getRegistrationData', config);
            if(reply.hasOwnProperty('data') && reply.data.hasOwnProperty('data') && reply.data.hasOwnProperty('responseStatus') && reply.data.responseStatus === API_OK) {
                setState({
                    ...state,
                    email: reply.data.data.email
                });
            } else {
                if((reply.data.responseStatus === API_EXCEPTION) && (reply.data.hasOwnProperty("message"))){
                    errorMessage = reply.data["message"];
                }
                if(!checkApiSessionErrorMessage(errorMessage, dispatch, logout, navigate)) {
                    setState({
                        ...state,
                        errors: {server_error: errorMessage}
                    });
                }
            }
        } catch (e) {
            setState({
                ...state,
                errors: {server_error: errorMessage}
            });
        }
    }

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (retry.isWaiting) {
            return;
        }
        // validateForm() returns true when a field is invalid (it also
        // sets the field errors it found).
        if (validateForm()) {
            return;
        }

        let errorMessage = DEFAULT_ERROR_TEXT;

        let config = {'headers': {"token": userToken(), "lang": getLang()}};

        const data = {code: state.code.trim(), sendPhoneVerificationCode: true};

        // הטוקן מצורף כאן, כדי שהפעולה שהוא נוצר עבורה תיקבע במקום אחד.
        const payload = await captcha.attach(data);
        // אין טוקן - אין מה לשלוח. ההסבר כבר מצויר על המסך.
        if (!payload) {
            return;
        }

        try {
            const reply = await submit.run(() => axios.post(API_URL + 'checkEmailVerificationCode', payload, config));
            // A blocked second click returns nothing; the first one is still running.
            if (!reply) {
                return;
            }
            if(reply.hasOwnProperty('data') && reply.data.hasOwnProperty('responseStatus') && reply.data.responseStatus === API_OK){
                navigate("/verify-phone");
                return;
            }

            if((reply.data.responseStatus === API_EXCEPTION) && (reply.data.hasOwnProperty("message"))){
                errorMessage = reply.data["message"];
            }
            if(checkApiSessionErrorMessage(errorMessage, dispatch, logout, navigate)) {
                return;
            }

            let errors = {server_error: errorMessage};

            // Server reports the remaining lockout as fractional minutes;
            // turn it into a live countdown that gates the next button below.
            if((reply.data.responseStatus === API_EXCEPTION) && (reply.data.hasOwnProperty("tryAgainInMinutes"))){
                retry.startFromMinutes(reply.data["tryAgainInMinutes"]);
            }

            setState({
                ...state,
                errors: errors,
                message: null
            });
        } catch (e) {
            setState({
                ...state,
                errors: {server_error: errorMessage},
                message: null
            });
        }
    };


    const handleResendCode = async (e) => {
        e.preventDefault();
        if (retry.isWaiting) {
            return;
        }
        let errorMessage = DEFAULT_ERROR_TEXT;

        let config = {'headers': {"token": userToken(), "lang": getLang()}};
        // הטוקן מצורף כאן, כדי שהפעולה שהוא נוצר עבורה תיקבע במקום אחד.
        const payload = await captcha.attach({});
        // אין טוקן - אין מה לשלוח. ההסבר כבר מצויר על המסך.
        if (!payload) {
            return;
        }

        try {
            const reply = await submit.run(() => axios.post(API_URL + 'sendEmailVerificationCode', payload, config));
            if (!reply) {
                return;
            }
            if(reply.hasOwnProperty('data') && reply.data.hasOwnProperty('responseStatus') && reply.data.responseStatus === API_OK){
                setState({
                    ...state,
                    errors: {},
                    message: "Email verification code sent"
                });
                // קוד חדש לאותו שדה - והסמן חוזר אליו מהקישור שבתחתית המסך.
                focusCode();
            }else{
                if((reply.data.responseStatus === API_EXCEPTION) && (reply.data.hasOwnProperty("message"))){
                    errorMessage = reply.data["message"];
                }
                if(!checkApiSessionErrorMessage(errorMessage, dispatch, logout, navigate)) {
                    let errors = {server_error: errorMessage};

                    // Server reports the remaining lockout as fractional minutes;
                    // turn it into a live countdown that gates the resend/next buttons.
                    if((reply.data.responseStatus === API_EXCEPTION) && (reply.data.hasOwnProperty("tryAgainInMinutes"))){
                        retry.startFromMinutes(reply.data["tryAgainInMinutes"]);
                    }

                    setState({
                        ...state,
                        errors: errors,
                        message: null
                    });
                }
            };
        } catch (e) {
            setState({
                ...state,
                errors: {server_error: errorMessage},
                message: null
            });
        }

    };

    const handleChange = (e) => {
        setState({
            ...state,
            [e.target.id]: e.target.value,
            errors: {},
            message: null
        });
    };

    const validateForm = () => {
        const errors = {};
        if (!state.code.trim() /*|| state.firstName.trim().length < 4*/) {
            errors['code'] = 'Verification Code is required';
        }

        setState({
            ...state,
            errors: errors,
            message: null
        });

        return Object.keys(errors).length > 0;
    };

    /**
     * Enter ממשיך לשלב הבא. ראה `useEnterSubmit` - הפעולה הראשית של המסך אינה
     * כפתור שליחה אלא `div`, ולכן הדפדפן אינו שולח כאן דבר מעצמו.
     */
    useEnterSubmit((e) => handleSubmit(e), !submit.busy);

    return (
        <Frame>
            <div className='main main-verify-phone'>
                <div className='main-top'>
                    <h1>{t("Verify your email")}</h1>
                </div>
                <div className='main-body'>
                    <div className='right-main'>
                        <div className='right-main-inner'>
                            {/* הכתובת מבודדת בכיווניות: היא אינה עברית, ובתוך משפט עברי הנקודות והסימנים
                                שלה מתחלפים במקומם. אותה תבנית כמו במסך הטלפון ובמסך האימות. */}
                            <h2><Trans i18nKey="We just sent a verification code to <span>{{email}}</span>. Please insert it here" values={{email: state.email}} components={{span: <span className="ltr-span" />}} /></h2>
                            <div className='mt30 input blue-border'><input ref={codeRef} className={state.errors.code?"error":""}  type="text" id="code" inputMode="numeric" autoComplete="one-time-code" placeholder={t("Verification Code")} onChange={(e) => handleChange(e)} /></div>
                            {captcha.node}
                            <div {...submit.buttonProps({base: 'button button-space-between button-teal', blocked: retry.isWaiting})} onClick={(e) => handleSubmit(e)}>  <div className='mr20'>{submit.busy ? t("Checking the code...") : t("next")}</div> {submit.busy ? <span className='button-spinner ml20' /> : <img src='/images/less-than-white.png' className='ml20 button-icon less-than-icon' alt=''/>} </div>
                            <h2 {...submit.buttonProps({base: 'mt40 teal pointer', blocked: retry.isWaiting})} onClick={(e) => handleResendCode(e)}>{submit.busy ? <>{t("Sending...")}<span className='button-spinner' /></> : <Trans i18nKey="I didn't receive the code, <strong>please resend it</strong>" />}</h2>
                            {state.message?<h2 className='teal'><Trans i18nKey={state.message} /></h2>:""}
                            {Object.entries(state.errors).map(([key, msg]) => (
                                <h2 key={key} className='mt20 error'><Trans i18nKey={msg} /></h2>
                            ))}
                            <RetryNotice retry={retry} />

                            {/* {state.error?<h2 className='mt10 error'><Trans i18nKey="kk4" /></h2>:""}
                            {state.error?<h2 className='error'><Trans i18nKey="kk5" /></h2>:""}
                            {state.error?<h2><Trans i18nKey="kk6" /></h2>:""} */}
                        </div>
                    </div>    
                    <div className='left-main'>
                        <img src='/images/person-computer.png' className='content-logo' alt=''/>
                    </div>
                </div>
            </div>
        </Frame>
    );
}